import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import { allActionsStore } from "../../stores/actions/all_actions_store"
import { WILDCARD } from "../../lib/constants/permissions_constants"

/**
 * ActionsTreeController manages the hierarchical actions tree in the permissions modal
 *
 * Features:
 * - Renders actions grouped by resource type from cached allActionsStore data
 * - Search functionality across identifier, name, AND description with debouncing
 * - Checkbox selection with resource wildcard support
 * - Expand/collapse resource type nodes
 *
 * Connects to data-controller="trees--actions-tree"
 */
export default class extends Controller {

    static stores = [
        allActionsStore
    ]

    static targets = [
        "treeContainer",
        "searchInput",
        "searchClearButton",
        "loadingIndicator",
        "errorMessage"
    ]

    static values = {
        userId: String,
        currentSelections: Array  // Pre-selected actions from user policies
    }

    // Lifecycle methods
    connect() {
        useStore(this)
        
        // Initialize state
        this.expandedResources = new Set()
        this.selectedActions = new Set(this.currentSelectionsValue || [])
        this.searchQuery = ''
        this.searchDebounceTimer = null
        
        // Listen for modal open event
        this.boundHandleModalOpen = this.handleModalOpen.bind(this)
        window.addEventListener('forms--permissions-form:opened', this.boundHandleModalOpen)
        
        // Render tree from cached data
        this.renderTreeFromStore()
    }

    disconnect() {
        // Cleanup
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer)
        }
        
        // Remove event listener
        window.removeEventListener('forms--permissions-form:opened', this.boundHandleModalOpen)
    }

    // Value change callback
    currentSelectionsValueChanged(newValue) {
        // Update internal state when value changes from parent controller
        this.selectedActions = new Set(newValue || [])
        
        // Only render if store is ready
        const storeData = this.allActionsStoreValue
        if (storeData && storeData.initialLoadComplete) {
            this.renderTreeFromStore()
        }
    }
    
    // Event Handlers
    handleModalOpen(event) {
        const { userId, currentPolicies } = event.detail
        
        // Update user ID
        this.userIdValue = userId
        
        // Update current selections from policies
        if (currentPolicies && currentPolicies.actions) {
            this.selectedActions = new Set(currentPolicies.actions)
            this.currentSelectionsValue = currentPolicies.actions
        }
        
        // Re-render tree with updated selections
        this.renderTreeFromStore()
    }

    // Store Integration
    onAllActionsStoreUpdate(newValue) {
        // Re-render tree when store updates
        if (newValue.initialLoadComplete) {
            this.renderTreeFromStore()
        }
    }

    // Rendering Methods
    renderTreeFromStore() {
        const storeData = this.allActionsStoreValue
        
        // Safety check: if store is undefined, show loading
        if (!storeData) {
            this.showLoadingIndicator()
            return
        }
        
        if (!storeData.initialLoadComplete) {
            // Store not loaded yet - show loading indicator
            // The all_actions_supplier_controller will populate it
            this.showLoadingIndicator()
            return
        }
        
        this.hideLoadingIndicator()
        this.hideError()
        
        const actions = storeData.actions || []
        this.renderTree(actions)
    }

    getActionsFromStore() {
        const storeData = this.allActionsStoreValue
        return storeData.actions || []
    }

    renderTree(actions) {
        if (!this.hasTreeContainerTarget) return
        
        if (actions.length === 0) {
            this.treeContainerTarget.innerHTML = '<p class="text-muted">No actions available</p>'
            return
        }
        
        // Group actions by resource type
        const groupedActions = this.groupActionsByResource(actions)
        
        // Sort resource types alphabetically
        const sortedResources = Object.keys(groupedActions).sort()
        
        const treeHTML = sortedResources.map(resource => 
            this.buildResourceNodeHTML(resource, groupedActions[resource])
        ).join('')
        
        this.treeContainerTarget.innerHTML = `<div role="tree">${treeHTML}</div>`
    }

    groupActionsByResource(actions) {
        const grouped = {}
        
        actions.forEach(action => {
            // Extract resource type from identifier (e.g., "items:create" -> "items")
            const parts = action.identifier.split(':')
            const resource = parts[0] || 'other'
            
            if (!grouped[resource]) {
                grouped[resource] = []
            }
            
            grouped[resource].push(action)
        })
        
        // Sort actions within each resource alphabetically by description
        Object.keys(grouped).forEach(resource => {
            grouped[resource].sort((a, b) => 
                (a.description || a.identifier).localeCompare(b.description || b.identifier)
            )
        })
        
        return grouped
    }

    buildResourceNodeHTML(resource, actions) {
        const resourceId = `resource-${resource}`
        const isExpanded = this.expandedResources.has(resourceId)
        const wildcardPattern = `${resource}:${WILDCARD}`
        const isWildcardSelected = this.selectedActions.has(wildcardPattern)
        
        const expandIcon = isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'
        
        let html = `
            <div class="tree-node resource-node" 
                 data-resource-id="${resourceId}"
                 role="treeitem"
                 aria-level="1"
                 aria-expanded="${isExpanded}">
                <span class="tree-node-expand" data-action="click->trees--actions-tree#toggleResource">
                    <i class="bi ${expandIcon}"></i>
                </span>
                <input type="checkbox" 
                       class="tree-node-checkbox resource-wildcard"
                       data-resource="${resource}"
                       ${isWildcardSelected ? 'checked' : ''}
                       data-action="change->trees--actions-tree#toggleResourceWildcard">
                <label class="tree-node-label">
                    <strong>${this.escapeHtml(this.formatResourceName(resource))}</strong>
                    <span class="text-muted ms-2">(${actions.length} actions)</span>
                </label>
            </div>
        `
        
        // Add action nodes if expanded
        if (isExpanded) {
            html += actions.map(action => this.buildActionNodeHTML(action, resource)).join('')
        }
        
        return html
    }

    buildActionNodeHTML(action, resource) {
        const isSelected = this.selectedActions.has(action.identifier)
        const wildcardPattern = `${resource}:${WILDCARD}`
        const isDisabled = this.selectedActions.has(wildcardPattern)
        
        const indentStyle = 'padding-left: 1.5rem;'
        
        return `
            <div class="tree-node action-node" 
                 data-action-id="${action.identifier}"
                 role="treeitem"
                 aria-level="2"
                 style="${indentStyle}">
                <span class="tree-node-spacer"></span>
                <input type="checkbox" 
                       class="tree-node-checkbox action-checkbox"
                       data-action-identifier="${action.identifier}"
                       data-resource="${resource}"
                       ${isSelected ? 'checked' : ''}
                       ${isDisabled ? 'disabled' : ''}
                       data-action="change->trees--actions-tree#toggleAction">
                <label class="tree-node-label ${isDisabled ? 'text-muted' : ''}">
                    ${this.escapeHtml(action.description || action.identifier)}
                </label>
            </div>
        `
    }

    formatResourceName(resource) {
        // Convert "items" to "Items", "device_types" to "Device Types"
        return resource
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')
    }

    escapeHtml(text) {
        const div = document.createElement('div')
        div.textContent = text
        return div.innerHTML
    }

    // Tree Interaction Methods
    toggleResource(event) {
        event.preventDefault()
        const nodeElement = event.target.closest('.tree-node')
        if (!nodeElement) return
        
        const resourceId = nodeElement.dataset.resourceId
        
        if (this.expandedResources.has(resourceId)) {
            this.expandedResources.delete(resourceId)
        } else {
            this.expandedResources.add(resourceId)
        }
        
        this.renderTreeFromStore()
    }

    // Checkbox Management
    toggleAction(event) {
        const checkbox = event.target
        const actionIdentifier = checkbox.dataset.actionIdentifier
        
        if (!actionIdentifier) return
        
        if (checkbox.checked) {
            // Add action to selections
            this.selectedActions.add(actionIdentifier)
        } else {
            // Remove action from selections
            this.selectedActions.delete(actionIdentifier)
        }
        
        // Update the value for external access
        this.currentSelectionsValue = Array.from(this.selectedActions)

        // Dispatch event to parent controller
        this.dispatch('selectionChanged', { detail: { selectedActions: Array.from(this.selectedActions) } })
    }

    toggleResourceWildcard(event) {
        const checkbox = event.target
        const resource = checkbox.dataset.resource
        const wildcardPattern = `${resource}:${WILDCARD}`

        if (!resource) return

        if (checkbox.checked) {
            // Add wildcard to selections
            this.selectedActions.add(wildcardPattern)

            // Remove redundant individual actions
            this.removeRedundantActions(resource)

            // Disable individual action checkboxes
            this.disableResourceActions(resource, true)
        } else {
            // Remove wildcard from selections
            this.selectedActions.delete(wildcardPattern)

            // Re-enable individual action checkboxes
            this.disableResourceActions(resource, false)
        }

        // Update the value for external access
        this.currentSelectionsValue = Array.from(this.selectedActions)

        // Dispatch event to parent controller
        this.dispatch('selectionChanged', { detail: { selectedActions: Array.from(this.selectedActions) } })

        // Re-render to update UI
        this.renderTreeFromStore()
    }

    removeRedundantActions(resource) {
        // Remove all individual actions for this resource
        const actionsToRemove = []
        
        this.selectedActions.forEach(identifier => {
            if (identifier.startsWith(resource + ':') && !identifier.endsWith(':' + WILDCARD)) {
                actionsToRemove.push(identifier)
            }
        })
        
        actionsToRemove.forEach(identifier => this.selectedActions.delete(identifier))
    }

    disableResourceActions(resource, disabled) {
        // Find all checkboxes for this resource and disable/enable them
        if (!this.hasTreeContainerTarget) return
        
        const checkboxes = this.treeContainerTarget.querySelectorAll('.action-checkbox')
        
        checkboxes.forEach(checkbox => {
            const checkboxResource = checkbox.dataset.resource
            if (checkboxResource === resource) {
                checkbox.disabled = disabled
                
                // Update visual styling
                const nodeElement = checkbox.closest('.tree-node')
                if (nodeElement) {
                    const label = nodeElement.querySelector('.tree-node-label')
                    if (label) {
                        if (disabled) {
                            label.classList.add('text-muted')
                        } else {
                            label.classList.remove('text-muted')
                        }
                    }
                }
            }
        })
    }

    getSelectedActions() {
        return Array.from(this.selectedActions)
    }

    // Search Methods
    handleSearchInput(event) {
        const query = event.target.value
        
        // Clear existing debounce timer
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer)
        }
        
        // Debounce search by 300ms
        this.searchDebounceTimer = setTimeout(() => {
            this.performSearch(query)
        }, 300)
        
        // Show/hide clear button
        if (this.hasSearchClearButtonTarget) {
            if (query.length > 0) {
                this.searchClearButtonTarget.classList.remove('d-none')
            } else {
                this.searchClearButtonTarget.classList.add('d-none')
            }
        }
    }

    clearSearch() {
        // Clear search input
        if (this.hasSearchInputTarget) {
            this.searchInputTarget.value = ''
        }
        
        // Hide clear button
        if (this.hasSearchClearButtonTarget) {
            this.searchClearButtonTarget.classList.add('d-none')
        }
        
        // Clear search query and restore tree
        this.searchQuery = ''
        this.performSearch('')
    }

    performSearch(query) {
        this.searchQuery = query.toLowerCase().trim()
        
        if (this.searchQuery === '') {
            // No search - render full tree
            this.renderTreeFromStore()
        } else {
            // Filter tree to show only matches and parent resources
            this.filterTree(this.searchQuery)
        }
    }

    filterTree(query) {
        const actions = this.getActionsFromStore()
        
        if (actions.length === 0) {
            return
        }
        
        // Find all matching actions
        const matchingActions = actions.filter(action => this.matchesSearch(action, query))
        
        if (matchingActions.length === 0) {
            // No matches found
            this.treeContainerTarget.innerHTML = '<p class="text-muted">No actions found</p>'
            return
        }
        
        // Group matching actions by resource
        const groupedActions = this.groupActionsByResource(matchingActions)
        
        // Auto-expand all resource nodes that have matches
        Object.keys(groupedActions).forEach(resource => {
            this.expandedResources.add(`resource-${resource}`)
        })
        
        // Render filtered tree with highlighting
        this.renderFilteredTree(groupedActions, query)
    }

    matchesSearch(action, query) {
        // CRITICAL: Search across identifier, name (description), AND description
        const identifier = (action.identifier || '').toLowerCase()
        const description = (action.description || '').toLowerCase()
        
        return identifier.includes(query) || description.includes(query)
    }

    renderFilteredTree(groupedActions, query) {
        if (!this.hasTreeContainerTarget) return
        
        // Sort resource types alphabetically
        const sortedResources = Object.keys(groupedActions).sort()
        
        const treeHTML = sortedResources.map(resource => 
            this.buildFilteredResourceNodeHTML(resource, groupedActions[resource], query)
        ).join('')
        
        this.treeContainerTarget.innerHTML = `<div role="tree">${treeHTML}</div>`
    }

    buildFilteredResourceNodeHTML(resource, actions, query) {
        const resourceId = `resource-${resource}`
        const isExpanded = this.expandedResources.has(resourceId)
        const wildcardPattern = `${resource}:${WILDCARD}`
        const isWildcardSelected = this.selectedActions.has(wildcardPattern)
        
        const expandIcon = isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'
        
        let html = `
            <div class="tree-node resource-node" 
                 data-resource-id="${resourceId}"
                 role="treeitem"
                 aria-level="1"
                 aria-expanded="${isExpanded}">
                <span class="tree-node-expand" data-action="click->trees--actions-tree#toggleResource">
                    <i class="bi ${expandIcon}"></i>
                </span>
                <input type="checkbox" 
                       class="tree-node-checkbox resource-wildcard"
                       data-resource="${resource}"
                       ${isWildcardSelected ? 'checked' : ''}
                       data-action="change->trees--actions-tree#toggleResourceWildcard">
                <label class="tree-node-label">
                    <strong>${this.escapeHtml(this.formatResourceName(resource))}</strong>
                    <span class="text-muted ms-2">(${actions.length} matching)</span>
                </label>
            </div>
        `
        
        // Add action nodes if expanded
        if (isExpanded) {
            html += actions.map(action => this.buildFilteredActionNodeHTML(action, resource, query)).join('')
        }
        
        return html
    }

    buildFilteredActionNodeHTML(action, resource, query) {
        const isSelected = this.selectedActions.has(action.identifier)
        const wildcardPattern = `${resource}:${WILDCARD}`
        const isDisabled = this.selectedActions.has(wildcardPattern)
        
        const indentStyle = 'padding-left: 1.5rem;'
        
        // Highlight matching text in description
        const highlightedDescription = this.highlightMatches(
            action.description || action.identifier, 
            query
        )
        
        return `
            <div class="tree-node action-node" 
                 data-action-id="${action.identifier}"
                 role="treeitem"
                 aria-level="2"
                 style="${indentStyle}">
                <span class="tree-node-spacer"></span>
                <input type="checkbox" 
                       class="tree-node-checkbox action-checkbox"
                       data-action-identifier="${action.identifier}"
                       data-resource="${resource}"
                       ${isSelected ? 'checked' : ''}
                       ${isDisabled ? 'disabled' : ''}
                       data-action="change->trees--actions-tree#toggleAction">
                <label class="tree-node-label ${isDisabled ? 'text-muted' : ''}">
                    ${highlightedDescription}
                </label>
            </div>
        `
    }

    highlightMatches(text, query) {
        if (!query) return this.escapeHtml(text)
        
        const escapedText = this.escapeHtml(text)
        const regex = new RegExp(`(${this.escapeRegex(query)})`, 'gi')
        return escapedText.replace(regex, '<mark class="search-highlight">$1</mark>')
    }

    escapeRegex(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    }

    // UI Helper Methods
    showLoadingIndicator() {
        if (this.hasLoadingIndicatorTarget) {
            this.loadingIndicatorTarget.classList.remove('d-none')
        }
    }

    hideLoadingIndicator() {
        if (this.hasLoadingIndicatorTarget) {
            this.loadingIndicatorTarget.classList.add('d-none')
        }
    }

    showError(message) {
        if (this.hasErrorMessageTarget) {
            this.errorMessageTarget.innerHTML = this.escapeHtml(message)
            this.errorMessageTarget.classList.remove('d-none')
        }
    }

    hideError() {
        if (this.hasErrorMessageTarget) {
            this.errorMessageTarget.classList.add('d-none')
            this.errorMessageTarget.innerHTML = ''
        }
    }

    /**
     * Show error message with retry button
     * @param {string} message - User-friendly error message
     * @param {Function} retryCallback - Function to call when retry button is clicked
     */
    showErrorWithRetry(message, retryCallback) {
        if (this.hasErrorMessageTarget) {
            const retryButton = `
                <button type="button" 
                        class="btn btn-sm btn-primary ms-2" 
                        data-action="click->trees--actions-tree#handleRetry">
                    Try Again
                </button>
            `
            this.errorMessageTarget.innerHTML = `
                <div class="d-flex align-items-center justify-content-between">
                    <span>${this.escapeHtml(message)}</span>
                    ${retryButton}
                </div>
            `
            this.errorMessageTarget.classList.remove('d-none')
            
            // Store retry callback for later use
            this.retryCallback = retryCallback
        }
    }

    /**
     * Handle retry button click
     */
    handleRetry(event) {
        event.preventDefault()
        this.hideError()
        
        if (this.retryCallback && typeof this.retryCallback === 'function') {
            this.retryCallback()
        }
    }

    /**
     * Handle API errors based on status code
     * @param {number} status - HTTP status code
     */
    handleApiError(status) {
        let message = ''
        
        switch (status) {
            case 401:
            case 403:
                message = "You don't have permission to view this data."
                this.showError(message)
                break
            case 404:
                message = "Requested data not found."
                this.showError(message)
                break
            case 408:
            case 504:
                message = "Request timed out. Please try again."
                this.showErrorWithRetry(message, () => {
                    // Trigger refresh from supplier controller
                    const supplierController = this.application.getControllerForElementAndIdentifier(
                        document.querySelector('[data-controller*="suppliers--actions--all-actions-supplier"]'),
                        'suppliers--actions--all-actions-supplier'
                    )
                    if (supplierController && supplierController.refreshActions) {
                        supplierController.refreshActions()
                    }
                })
                break
            case 500:
            case 502:
            case 503:
                message = "Server error occurred. Please try again later."
                this.showErrorWithRetry(message, () => {
                    // Trigger refresh from supplier controller
                    const supplierController = this.application.getControllerForElementAndIdentifier(
                        document.querySelector('[data-controller*="suppliers--actions--all-actions-supplier"]'),
                        'suppliers--actions--all-actions-supplier'
                    )
                    if (supplierController && supplierController.refreshActions) {
                        supplierController.refreshActions()
                    }
                })
                break
            default:
                message = "An error occurred. Please try again."
                this.showErrorWithRetry(message, () => {
                    // Trigger refresh from supplier controller
                    const supplierController = this.application.getControllerForElementAndIdentifier(
                        document.querySelector('[data-controller*="suppliers--actions--all-actions-supplier"]'),
                        'suppliers--actions--all-actions-supplier'
                    )
                    if (supplierController && supplierController.refreshActions) {
                        supplierController.refreshActions()
                    }
                })
        }
        
        console.error('API Error:', {
            status,
            message,
            timestamp: new Date().toISOString()
        })
    }
}
